import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { normalizeUserGuid } from '@wlb/shared/utils/navigateToUserProfile';
import { ToastService } from '@wlb/shared/platform/notifier';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useCurrentProfile } from './useCurrentProfile';

/**
 * Web counterpart of the shared `navigateToUserProfile` helper, which speaks
 * React Navigation. Same rules: own GUID opens My Profile, another member's
 * opens their profile, a missing GUID toasts instead of navigating.
 */
export const useOpenUserProfile = (): ((userGuid?: string | null, options?: { state?: any }) => boolean) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentProfile = useCurrentProfile();
  const currentUserGuid = currentProfile?.userGuid;

  return useCallback(
    (userGuid?: string | null, options?: { state?: any }) => {
      const targetGuid = normalizeUserGuid(userGuid);
      if (!targetGuid) {
        ToastService.show({ type: 'error', message: t('profile.toast.unableToLoadProfile') });
        return false;
      }

      const selfGuid = normalizeUserGuid(currentUserGuid);
      navigate(
        selfGuid && selfGuid.toLowerCase() === targetGuid.toLowerCase()
          ? '/profile'
          : `/profile/others/${targetGuid}`,
        options,
      );
      return true;
    },
    [navigate, t, currentUserGuid],
  );
};
